import { startHfLogin } from '@/lib/api/auth'
import { rememberReturnTo } from '@/lib/auth/redirect'
import { useAuthModalStore } from '@/stores/authModalStore'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { useState } from 'react'

/**
 * LoginModal — Phase 1 은 HF SSO 단일 진입점.
 *
 * GDEV-263 (238a) 초안에 있던 disabled 이메일/비밀번호 placeholder + 가입하기
 * 링크는 미사용으로 제거 (5/21). HF OAuth 외 진입로는 Phase 2 에 재도입 검토.
 */
export function LoginModal() {
  const isOpen = useAuthModalStore((s) => s.isOpen)
  const mode = useAuthModalStore((s) => s.mode)
  const message = useAuthModalStore((s) => s.message)
  const close = useAuthModalStore((s) => s.close)
  const returnTo = useAuthModalStore((s) => s.returnTo)
  const show = isOpen && mode === 'signin'
  const [error, setError] = useState<string | null>(null)

  const handleHfLogin = async (): Promise<void> => {
    setError(null)
    try {
      // HF 로 전체 페이지 이동을 하므로 인메모리 returnTo 는 사라진다.
      // 왕복을 견디도록 저장해 둔다 (QA H-7).
      rememberReturnTo(returnTo)
      await startHfLogin()
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'HuggingFace 로그인을 시작할 수 없습니다.'
      setError(message)
    }
  }

  return (
    <DialogPrimitive.Root open={show} onOpenChange={(open) => !open && close()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[rgba(10,14,22,0.55)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
        <DialogPrimitive.Content className="fixed left-[50%] top-[50%] z-50 w-[440px] max-w-[calc(100vw-40px)] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-md border-0 bg-white shadow-[0_20px_60px_rgba(0,0,0,0.25)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0">
          <DialogPrimitive.Close
            aria-label="로그인 모달 닫기 (ESC)"
            className="absolute right-4 top-4 z-10 rounded-sm border-0 bg-transparent text-text-muted outline-none hover:text-text-base focus-visible:ring-2 focus-visible:ring-primary"
          >
            <X className="h-4 w-4" />
          </DialogPrimitive.Close>

          {/* 헤더 */}
          <div className="px-8 pt-8 text-center">
            <div
              className="mx-auto mb-5 grid h-12 w-12 place-items-center rounded-md"
              style={{ background: 'var(--color-brand-deep)' }}
              aria-hidden="true"
            >
              <span className="text-[24px] font-black text-white">R</span>
            </div>
            <DialogPrimitive.Title className="m-0 mb-2 text-[22px] font-extrabold text-text-base">
              로그인
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="m-0 text-[14px] leading-relaxed text-text-muted">
              {message ?? '로봇 지능의 시작점에 접속하세요'}
            </DialogPrimitive.Description>
          </div>

          {/* 바디 */}
          <div className="px-8 pb-8 pt-6">
            {/* HF SSO (Primary) */}
            <button
              type="button"
              onClick={() => {
                void handleHfLogin()
              }}
              className="flex w-full items-center justify-center gap-2.5 rounded-[4px] border-2 border-hf-sso-border bg-hf-sso-bg px-5 py-[13px] text-[15px] font-bold text-hf-sso-text transition hover:brightness-95"
              aria-label="Hugging Face 계정으로 로그인"
            >
              <span aria-hidden="true" className="text-[20px] leading-none">
                🤗
              </span>
              Hugging Face 계정으로 로그인
            </button>

            {error && <p className="mt-3 text-center text-[12px] text-danger">{error}</p>}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
